<template>
  <div class="container-fluid mt-3">

    <!-- Page Header -->
    <div class="body-title mb-3">
      <b-row class="no-gutters align-items-center">
        <b-col>
          <h5 class="dash-heading">Sub Company Report</h5>
        </b-col>
      </b-row>
    </div>

    <!-- Filter Card -->
    <b-card class="mb-3" header-bg-variant="dark" header-text-variant="white">
      <template v-slot:header>
        <strong>Search Filters</strong>
      </template>

      <b-row>
        <!-- Sub Company -->
        <b-col md="4">
          <b-form-group label="Sub Company" label-size="sm" label-cols="4">
            <b-form-select
              v-model="selectedCompanyID"
              :options="subCompanies"
              value-field="id"
              text-field="name"
              size="sm"
            >
              <template v-slot:first>
                <b-form-select-option :value="null">-- Select Sub Company --</b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>

        <!-- Financial Year -->
        <b-col md="3">
          <b-form-group label="Financial Year" label-size="sm" label-cols="4">
            <b-form-select
              v-model="selectedYear"
              :options="financialYears"
              size="sm"
            >
              <template v-slot:first>
                <b-form-select-option :value="null">-- Select Year --</b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>

        <!-- Scheme (Loan Type) -->
        <b-col md="3">
          <b-form-group label="Scheme" label-size="sm" label-cols="3">
            <b-form-select
              v-model="selectedLoanType"
              :options="loanTypeOptions"
              value-field="value"
              text-field="text"
              size="sm"
            ></b-form-select>
          </b-form-group>
        </b-col>

        <!-- Search Button -->
        <b-col md="2" class="d-flex align-items-center">
          <b-button
            variant="primary"
            size="sm"
            :disabled="!selectedCompanyID || !selectedYear || isLoading"
            @click="fetchReport"
          >
            <span v-if="isLoading">
              <b-spinner small></b-spinner> Loading...
            </span>
            <span v-else>Search</span>
          </b-button>
        </b-col>
      </b-row>

      <!-- Caste chips for selected company -->
      <b-row v-if="selectedCompany" class="mt-1">
        <b-col>
          <small class="text-muted mr-2"><strong>Castes:</strong></small>
          <b-badge
            v-for="c in selectedCompany.castes"
            :key="c.casteCode"
            variant="secondary"
            class="mr-1"
            style="font-size:11px"
          >{{ c.casteCode }} - {{ c.casteName }}</b-badge>
        </b-col>
      </b-row>

    </b-card>

    <!-- Always-visible LOI Summary -->
    <b-card v-if="selectedCompanyID && selectedYear" class="mb-3" body-class="py-2 px-3" style="background:#f8fafc;border:1px solid #e0e0e0;">
      <div v-if="loiSummaryLoading" class="text-center py-2">
        <b-spinner small></b-spinner> Loading summary...
      </div>
      <div v-else>
        <span class="mr-4"><strong>Total LOI Issued:</strong> {{ loiSummary.count }}</span>
        <span><strong>Total Amount Approved:</strong> {{ loiSummary.amount.toLocaleString('en-IN', {minimumFractionDigits:2}) }}</span>
      </div>
    </b-card>

    <!-- Results Card -->
    <b-card v-if="reportData.length > 0 || searched" header-bg-variant="primary" header-text-variant="white">
      <template v-slot:header>
        <b-row class="no-gutters align-items-center">
          <b-col>
            <strong v-if="selectedCompany">{{ selectedCompany.name }} — {{ selectedYear }}</strong>
          </b-col>
          <b-col class="text-right">
            <!-- Quick LOI Summary button removed: summary is always visible now -->
            <b-badge variant="light" class="mr-3" style="color:#333;font-size:13px;">
              Total: {{ totalRows }}
            </b-badge>
            <b-button
              variant="success"
              size="sm"
              :disabled="reportData.length === 0"
              @click="exportToExcel"
            >
              <i class="fa fa-file-excel-o"></i> Export Excel
            </b-button>
          </b-col>
        </b-row>
      </template>

      <!-- No data message -->
      <b-alert show variant="warning" v-if="searched && reportData.length === 0">
        No applications found for the selected sub company and financial year.
      </b-alert>

      <!-- Table -->
      <div v-if="reportData.length > 0">
            <!-- Quick LOI Summary Modal removed: summary is always visible now -->
        <!-- Per page + filter row -->
        <b-row class="mb-2">
          <b-col md="3">
            <b-input-group size="sm">
              <b-form-input v-model="tableFilter" placeholder="Search in table..." />
              <b-input-group-append>
                <b-btn :disabled="!tableFilter" @click="tableFilter=''" size="sm">Clear</b-btn>
              </b-input-group-append>
            </b-input-group>
          </b-col>
          <b-col md="3" offset-md="6">
            <b-form-group horizontal label="Per page" class="mb-0" label-size="sm" label-cols="4">
              <b-form-select :options="pageOptions" v-model="perPage" size="sm" />
            </b-form-group>
          </b-col>
        </b-row>

        <div class="overflow-auto">
          <b-table
            show-empty
            head-variant="dark"
            bordered
            striped
            small
            hover
            style="font-size:12px"
            :items="reportData"
            :fields="tableFields"
            :current-page="currentPage"
            :per-page="perPage"
            :filter="tableFilter"
            @filtered="onFiltered"
            empty-text="No records available."
          >
            <template v-slot:cell(Sr)="row">{{ (currentPage - 1) * perPage + row.index + 1 }}</template>
          </b-table>
        </div>

        <!-- Totals Row -->
        <div class="overflow-auto mt-2">
          <table class="table table-bordered table-sm" style="font-size:12px;background:#f0f7ff;">
            <tr style="font-weight:bold;">
              <td colspan="8" class="text-right">TOTAL:</td>
              <td>{{ sumAmountDemanded }}</td>
              <td colspan="2"></td>
              <td>{{ sumAmountApproved }}</td>
              <td colspan="4"></td>
              <td>{{ sumDemand }}</td>
              <td>{{ sumClaimAmount }}</td>
              <td></td>
              <td>{{ sumPaidAmt }}</td>
              <td></td>
              <td></td>
            </tr>
          </table>
          <div class="mt-1" style="font-size:13px;">
            <strong>Total Applications:</strong> {{ totalApplications }} &nbsp; | &nbsp;
            <strong>Total LOI:</strong> {{ totalLOI }} &nbsp; | &nbsp;
            <strong>Total Claims:</strong> {{ totalClaim }}
          </div>
        </div>

        <!-- Pagination -->
        <b-row class="mt-2">
          <b-col md="5">
            <span style="font-size:0.875rem">Total Rows: {{ totalRows }}</span>
          </b-col>
          <b-col md="7">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              size="sm"
              style="float:right"
              limit="10"
            ></b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- Initial placeholder -->
    <b-card v-if="!searched && reportData.length === 0" class="text-center py-5">
      <p class="text-muted mb-0">Select a Sub Company and Financial Year, then click <strong>Search</strong> to view the report.</p>
    </b-card>

  </div>
</template>

<script>
import apis from "@/apis";
import XLSX from "xlsx";

export default {
  name: "SubCompanyReport",
  data() {
    return {
      selectedCompanyID: null,
      selectedYear: null,
      selectedLoanType: 1,
      isLoading: false,
      searched: false,
      reportData: [],
      tableFilter: "",
      currentPage: 1,
      perPage: 50,
      totalRows: 0,
      pageOptions: [50, 100, 200, 500],
      loanTypeOptions: [
        { value: 1, text: "Individual Loan" },
        { value: 2, text: "Group Loan" },
        { value: 3, text: "Education Loan" },
      ],
      tableFields: [
        { key: "Sr",                label: "Sr.No.",              thStyle: { width: "60px" } },
        { key: "CasteMarathi",      label: "CasteMarathi",        sortable: true },
        { key: "BusinessType",      label: "BusinessType",        sortable: true },
        { key: "SchemeType",        label: "SchemeType",          sortable: true },
        { key: "LoanApplicationNo", label: "LoanApplicationNo",   sortable: true },
        { key: "ADDRESS",           label: "ADDRESS",             sortable: true },
        { key: "Taluka",            label: "Taluka",              sortable: true },
        { key: "ApplicationDate",   label: "ApplicationDate",     sortable: true },
        { key: "AmountDemanded",    label: "AmountDemanded",      sortable: true },
        { key: "ApplicationStatus", label: "ApplicationStatus",   sortable: true },
        { key: "ApplicationStatus1",label: "ApplicationStatus1",  sortable: true },
        { key: "AmountApproved",    label: "AmountApproved",      sortable: true },
        { key: "LOINumber",         label: "LOINumber",           sortable: true },
        { key: "LOIDate",           label: "LOIDate",             sortable: true },
        { key: "LOIDueDate",        label: "LOIDueDate",          sortable: true },
        { key: "BankName",          label: "BankName",            sortable: true },
        { key: "Demand",            label: "Demand",              sortable: true },
        { key: "ClaimAmount",       label: "Claim Amount",        sortable: true },
        { key: "AppliedForClaimDtd",label: "Applied for Claim dtd", sortable: true },
        { key: "PaidAmt",           label: "Paid Amt",            sortable: true },
        { key: "Claim",             label: "Claim",               sortable: true },
        { key: "PaidDt",            label: "Paid dt",             sortable: true },
      ],
        showLoiSummary: false,
        loiSummaryLoading: false,
        loiSummary: { count: 0, amount: 0 },
      allSubCompanies: [
        {
          id: 1,
          tenantId: "spkemavm",
          url: "spkemavm.msobcfdc.in",
          name: "शामराव पेजे कोकण इतर मागासवर्ग आर्थिक विकास महामंडळ",
          nameEnglish: "Shamrao Peje Konkan OBC Economic Development Corporation",
          category: "Universal - सर्व इमाव जाती",
          type: "Universal",
          castes: [],
        },
        {
          id: 2,
          tenantId: "skgyavm",
          url: "skgyavm.msobcfdc.in",
          name: "संत काशिबा गुरव युवा आर्थिक विकास महामंडळ",
          nameEnglish: "Sant Kashiba Gurav Youth Economic Development Corporation",
          category: "गुरव",
          type: "Specialized",
          castes: [
            { casteCode: 43, casteName: "गुरव" },
          ],
        },
        {
          id: 3,
          tenantId: "jmbavm",
          url: "jmbavm.msobcfdc.in",
          name: "जगद्ज्योती महात्मा बसवेश्वर आर्थिक विकास महामंडळ",
          nameEnglish: "Jagadjyoti Mahatma Basaveshwar Economic Development Corporation",
          category: "वीरशैव - लिंगायत",
          type: "Specialized",
          castes: [
            { casteCode: 43,  casteName: "लिंगायत गुरव" },
            { casteCode: 58,  casteName: "जंगम, मालाजंगम (विरभद्र) लिंगायत जंगम" },
            { casteCode: 82,  casteName: "लिंगायत कुंभार" },
            { casteCode: 108, casteName: "लिंगायत न्हावी" },
            { casteCode: 125, casteName: "लिंगायत परीट, लिंगायत धोबी" },
            { casteCode: 127, casteName: "लिंगायत फुलारी" },
            { casteCode: 174, casteName: "लिंगायत सुतार" },
            { casteCode: 181, casteName: "लिंगायत तेली" },
            { casteCode: 182, casteName: "लिंगायत माळी" },
            { casteCode: 190, casteName: "लिंगायत वाणी व लिंगायत तांबोळी" },
            { casteCode: 338, casteName: "लिंगायत कुल्लेकडगी" },
          ],
        },
        {
          id: 4,
          tenantId: "ssmkm",
          url: "ssmkm.msobcfdc.in",
          name: "संत सेनाजी महाराज केशशिल्पी महामंडळ",
          nameEnglish: "Sant Senaji Maharaj Hairdresser Corporation",
          category: "न्हावी / केशशिल्पी",
          type: "Specialized",
          castes: [
            { casteCode: 0, casteName: "न्हावी" },
            { casteCode: 0, casteName: "हजाम" },
            { casteCode: 0, casteName: "महली" },
            { casteCode: 0, casteName: "म्हाली" },
            { casteCode: 0, casteName: "मारवाडी न्हावी" },
          ],
        },
        {
          id: 5,
          tenantId: "snmavmu",
          url: "snmavmu.msobcfdc.in",
          name: "संत नरहरी महाराज आर्थिक विकास महामंडळ",
          nameEnglish: "Sant Narhari Maharaj Economic Development Corporation",
          category: "सोनार",
          type: "Specialized",
          castes: [
            { casteCode: 0, casteName: "सोनार" },
            { casteCode: 0, casteName: "मारवाडी सोनार" },
            { casteCode: 0, casteName: "मारवाडी सुनार" },
            { casteCode: 0, casteName: "मारवाडी स्वर्णकार" },
          ],
        },
        {
          id: 6,
          tenantId: "sssmavmu",
          url: "sssmavmu.msobcfdc.in",
          name: "संत श्री संताजी महाराज आर्थिक विकास महामंडळ",
          nameEnglish: "Sant Shri Santaji Maharaj Economic Development Corporation",
          category: "तेली",
          type: "Specialized",
          castes: [
            { casteCode: 0, casteName: "तेली" },
            { casteCode: 0, casteName: "तिळवण तेली" },
            { casteCode: 0, casteName: "मराठा तेली" },
            { casteCode: 0, casteName: "तराणे तेली" },
            { casteCode: 0, casteName: "देशकर तेली" },
            { casteCode: 0, casteName: "एरंडेल तेली" },
            { casteCode: 0, casteName: "लिंगायत तेली" },
            { casteCode: 0, casteName: "एकबैल तेली" },
            { casteCode: 0, casteName: "दोन बैल तेली" },
            { casteCode: 0, casteName: "सावतेली" },
            { casteCode: 0, casteName: "एक बहिया तेली" },
            { casteCode: 0, casteName: "घाँची" },
          ],
        },
        {
          id: 7,
          tenantId: "ssrmavmu",
          url: "ssrmavmu.msobcfdc.in",
          name: "संत श्री. रुपलाल महाराज आर्थिक विकास महामंडळ",
          nameEnglish: "Sant Shri Ruplal Maharaj Economic Development Corporation",
          category: "बारी",
          type: "Specialized",
          castes: [
            { casteCode: 0, casteName: "बारी" },
            { casteCode: 0, casteName: "बारई" },
          ],
        },
        {
          id: 8,
          tenantId: "svrdlavmu",
          url: "svrdlavmu.msobcfdc.in",
          name: "स्व. विष्णूपंत रामचंद्र दादरे (लोणारी) आर्थिक विकास महामंडळ",
          nameEnglish: "Late Vishnupant Ramchandra Dadare (Lonari) Economic Development Corporation",
          category: "लोणारी",
          type: "Specialized",
          castes: [
            { casteCode: 0, casteName: "लोणारी" },
          ],
        },
        {
          id: 9,
          tenantId: "skavmu",
          url: "skavmu.msobcfdc.in",
          name: "सोळा कुलस्वामिनी आर्थिक विकास महामंडळ",
          nameEnglish: "Sola Kulswamini Economic Development Corporation",
          category: "वाणी",
          type: "Specialized",
          castes: [
            { casteCode: 0, casteName: "लाडशाखीय वाणी" },
            { casteCode: 0, casteName: "वाणी" },
            { casteCode: 0, casteName: "काथार" },
            { casteCode: 0, casteName: "काथार वाणी" },
            { casteCode: 0, casteName: "कंठहार वाणी" },
            { casteCode: 0, casteName: "वैश्यवाणी" },
            { casteCode: 0, casteName: "वैश्य-वाणी" },
            { casteCode: 0, casteName: "वै. वाणी" },
            { casteCode: 0, casteName: "वैश्य वाणी" },
            { casteCode: 0, casteName: "पानारी" },
            { casteCode: 0, casteName: "कुलवंत वाणी" },
            { casteCode: 0, casteName: "कु.वाणी" },
            { casteCode: 0, casteName: "वाणी (कुलवंत)" },
            { casteCode: 0, casteName: "कुलवंत-वाणी" },
            { casteCode: 0, casteName: "कुणबी वाणी" },
            { casteCode: 0, casteName: "नेवी" },
            { casteCode: 0, casteName: "धाकड" },
            { casteCode: 0, casteName: "मिटकरीवाणी" },
            { casteCode: 0, casteName: "बोरळ" },
            { casteCode: 0, casteName: "बोराळ" },
            { casteCode: 0, casteName: "बोरूळ" },
            { casteCode: 0, casteName: "बोरड" },
            { casteCode: 0, casteName: "तांबोळी" },
          ],
        },
        {
          id: 10,
          tenantId: "sssavmu",
          url: "sssavmu.msobcfdc.in",
          name: "श्री.संत नामदेव महाराज शिंपी समाज आर्थिक विकास महामंडळ",
          nameEnglish: "Shri Sant Namdev Maharaj Shimpi Samaj Economic Development Corporation",
          category: "शिंपी",
          type: "Specialized",
          castes: [
            { casteCode: 0, casteName: "शिंपी" },
            { casteCode: 0, casteName: "इद्रिसी / दर्जी" },
            { casteCode: 0, casteName: "साईसुतार" },
            { casteCode: 0, casteName: "जैन शिंपी" },
            { casteCode: 0, casteName: "श्रावक शिंपी" },
            { casteCode: 0, casteName: "शेतवाळ" },
            { casteCode: 0, casteName: "शेतवाल" },
            { casteCode: 0, casteName: "सैतवाल" },
            { casteCode: 0, casteName: "सैतवाळ" },
            { casteCode: 0, casteName: "मेरू शिंपी / मेरू क्षत्रिय शिंपी" },
            { casteCode: 0, casteName: "तेलगु दर्जी" },
            { casteCode: 0, casteName: "तेलगु शिंपी" },
            { casteCode: 0, casteName: "नामदेव शिंपी" },
            { casteCode: 0, casteName: "भावसार क्षत्रिय शिंपी" },
            { casteCode: 0, casteName: "भावगर" },
            { casteCode: 0, casteName: "शिव शिंपी" },
            { casteCode: 0, casteName: "नामदेव" },
          ],
        },
        {
          id: 11,
          tenantId: "sbkssavmu",
          url: "sbkssavmu.msobcfdc.in",
          name: "संत भोजलिंग काका सुतार समाज आर्थिक विकास महामंडळ",
          nameEnglish: "Sant Bhojling Kaka Sutar Samaj Economic Development Corporation",
          category: "सुतार",
          type: "Specialized",
          castes: [
            { casteCode: 0, casteName: "सुतार" },
            { casteCode: 0, casteName: "सुथार" },
            { casteCode: 0, casteName: "वाढई" },
            { casteCode: 0, casteName: "बाढी" },
            { casteCode: 0, casteName: "वढई" },
            { casteCode: 0, casteName: "बाढई" },
            { casteCode: 0, casteName: "बढई" },
            { casteCode: 0, casteName: "वाढी" },
            { casteCode: 0, casteName: "वाडी" },
            { casteCode: 0, casteName: "वधाई" },
            { casteCode: 0, casteName: "झाडे सुतार" },
            { casteCode: 0, casteName: "पांचाळ सुतार" },
            { casteCode: 0, casteName: "पांचाळ" },
            { casteCode: 0, casteName: "पंचाल" },
          ],
        },
        {
          id: 12,
          tenantId: "lpsavmu",
          url: "lpsavmu.msobcfdc.in",
          name: "लेवा पाटीदार समाज आर्थिक विकास महामंडळ",
          nameEnglish: "Leva Patidar Samaj Economic Development Corporation",
          category: "लेवा पटीदार",
          type: "Specialized",
          castes: [
            { casteCode: 0, casteName: "लेवा पटीदार" },
            { casteCode: 0, casteName: "लेवा कुणबी" },
            { casteCode: 0, casteName: "लेवा पाटील" },
          ],
        },
        {
          id: 13,
          tenantId: "svpgsavmu",
          url: "svpgsavmu.msobcfdc.in",
          name: "सरदार वल्लभभाई पटेल गुजर समाज आर्थिक विकास महामंडळ",
          nameEnglish: "Sardar Vallabhbhai Patel Gujar Samaj Economic Development Corporation",
          category: "गुजर",
          type: "Specialized",
          castes: [
            { casteCode: 0, casteName: "गुजर" },
            { casteCode: 0, casteName: "दोडे गुजर" },
            { casteCode: 0, casteName: "गुर्जर" },
            { casteCode: 0, casteName: "लेवा गुजर" },
            { casteCode: 0, casteName: "लेवे गुजर" },
            { casteCode: 0, casteName: "रेवे गुजर" },
            { casteCode: 0, casteName: "रेवा गुजर" },
            { casteCode: 0, casteName: "सुर्यवंशी गुजर" },
            { casteCode: 0, casteName: "बडगुजर तत्सम जाती लोंढारी" },
            { casteCode: 0, casteName: "बडगुजर तत्सम जाती पेंढारी" },
          ],
        },
        {
          id: 14,
          tenantId: "asavmu",
          url: "asavmu.msobcfdc.in",
          name: "आगरी समाज आर्थिक विकास महामंडळ",
          nameEnglish: "Agari Samaj Economic Development Corporation",
          category: "आगरी",
          type: "Specialized",
          castes: [
            { casteCode: 0, casteName: "आगरी" },
            { casteCode: 0, casteName: "आगळे" },
            { casteCode: 0, casteName: "काळण" },
          ],
        },
      ],
      subCompanies: [],
      currentTenantId: null,
      financialYears: [],
    };
  },
  computed: {
    selectedCompany() {
      if (!this.selectedCompanyID) return null;
      return this.subCompanies.find((c) => c.id === this.selectedCompanyID) || null;
    },
    sumAmountDemanded() {
      return this.reportData.reduce((s, r) => s + (parseFloat(r.AmountDemanded) || 0), 0).toFixed(2);
    },
    sumAmountApproved() {
      return this.reportData.reduce((s, r) => s + (parseFloat(r.AmountApproved) || 0), 0).toFixed(2);
    },
    sumDemand() {
      return this.reportData.reduce((s, r) => s + (parseFloat(r.Demand) || 0), 0).toFixed(2);
    },
    sumClaimAmount() {
      return this.reportData.reduce((s, r) => s + (parseFloat(r.ClaimAmount) || 0), 0).toFixed(2);
    },
    sumPaidAmt() {
      return this.reportData.reduce((s, r) => s + (parseFloat(r.PaidAmt) || 0), 0).toFixed(2);
    },
    totalApplications() {
      return this.reportData.length;
    },
    totalLOI() {
      return this.reportData.filter((r) => r.LOINumber && r.LOINumber.toString().trim() !== "").length;
    },
    totalClaim() {
      return this.reportData.filter((r) => (parseFloat(r.ClaimAmount) || 0) > 0).length;
    },
  },
  methods: {
    fetchLoiSummary() {
      if (!this.selectedCompanyID || !this.selectedYear) return;
      this.loiSummaryLoading = true;
      this.loiSummary = { count: 0, amount: 0 };

      const userDistrict = this.$store.getters.getUserDistrict;
      const districtID = (userDistrict && userDistrict !== "00000000-0000-0000-0000-000000000000")
        ? userDistrict
        : this.$store.getters.getDistrict;
      const { fyStart, fyEnd } = this.getDateRangeFromFY(this.selectedYear);

      const loiForm = {
        LoanApplicationNo: null,
        LOINumber: null,
        LOIStartDate: null,
        LOIEndDate: null,
        BankID: "00000000-0000-0000-0000-000000000000",
        DistrictID: districtID || "00000000-0000-0000-0000-000000000000",
        UserLoginID: null,
        RecordStartDate: null,
        RecordEndDate: null,
      };
      this.$apis.ReadSanctionLetterQuery(loiForm)
        .then((r) => {
          let data = (r.status === 200 ? r.body || [] : []);
          // Filter by FY
          data = data.filter((loi) => {
            const dt = new Date(loi.LOIDate);
            return dt >= fyStart && dt <= fyEnd;
          });
          this.loiSummary.count = data.length;
          this.loiSummary.amount = data.reduce((sum, loi) => sum + (parseFloat(loi.SanctionAmount) || 0), 0);
        })
        .catch(() => {
          this.loiSummary = { count: 0, amount: 0 };
        })
        .finally(() => {
          this.loiSummaryLoading = false;
        });
    },
      watch: {
        selectedCompanyID(val) {
          if (val && this.selectedYear) this.fetchLoiSummary();
        },
        selectedYear(val) {
          if (val && this.selectedCompanyID) this.fetchLoiSummary();
        }
      },
    detectTenantAndFilterCompanies() {
      const host = window.location.hostname.toLowerCase();
      const tenantIds = this.allSubCompanies.map((c) => c.tenantId);
      let matchedTenant = null;

      for (const tid of tenantIds) {
        if (host.startsWith(tid + ".")) {
          matchedTenant = tid;
          break;
        }
      }

      this.currentTenantId = matchedTenant;

      if (matchedTenant) {
        // On sub-company URL: show only that company
        const company = this.allSubCompanies.find((c) => c.tenantId === matchedTenant);
        this.subCompanies = company ? [company] : this.allSubCompanies;
        if (this.subCompanies.length === 1) {
          this.selectedCompanyID = this.subCompanies[0].id;
        }
      } else {
        // On main portal (msobcfdc.in) or localhost: show all sub-companies
        this.subCompanies = this.allSubCompanies;
      }
    },

    buildFinancialYears() {
      let today = new Date();
      let curr_year = today.getFullYear();
      if (today.getMonth() + 1 <= 3) curr_year -= 1;
      let list = [];
      while (curr_year >= 2020) {
        list.push(curr_year + "-" + (curr_year + 1));
        curr_year--;
      }
      this.financialYears = list;
    },

    getDateRangeFromFY(fy) {
      // fy = "2023-2024" → returns JS Date objects for client-side filtering
      const [startY] = fy.split("-").map(Number);
      const fyStart = new Date(startY, 3, 1);       // April 1
      const fyEnd   = new Date(startY + 1, 2, 31);  // March 31
      return { fyStart, fyEnd };
    },

    fetchReport() {
      if (!this.selectedCompanyID || !this.selectedYear) return;
      this.isLoading = true;
      this.searched  = false;
      this.reportData = [];

      const allowedStatuses = [
        "approved by gm",
        "approved by md",
        "loan in processing",
        "loi extension: approved by gm",
      ];

      const userDistrict = this.$store.getters.getUserDistrict;
      const districtID = (userDistrict && userDistrict !== "00000000-0000-0000-0000-000000000000")
        ? userDistrict
        : this.$store.getters.getDistrict;

      const casteCodes = this.selectedCompany.castes.map((c) => c.casteCode);
      const casteNames = this.selectedCompany.castes.map((c) =>
        c.casteName.trim().toLowerCase()
      );
      const { fyStart, fyEnd } = this.getDateRangeFromFY(this.selectedYear);

      // Helper: find first matching key value from an object
      const pick = (obj, keys, fallback) => {
        for (let k of keys) {
          if (obj[k] !== undefined && obj[k] !== null && obj[k] !== "") return obj[k];
        }
        return fallback;
      };

      // Step 1: Get status master to find IDs for our 4 target statuses
      apis.getApplicationStatusMstr()
        .then((statusResponse) => {
          let statusList = (statusResponse.status === 200) ? (statusResponse.body || []) : [];
          let targetStatusIds = statusList
            .filter((s) => allowedStatuses.includes((s.ApplicationStatus || "").trim().toLowerCase()))
            .map((s) => s.Id);
          if (targetStatusIds.length === 0) targetStatusIds = [1];

          console.log("Target status IDs:", targetStatusIds);

          // Step 2: Fire all calls in parallel — app calls + LOI + Claims
          const appPromises = targetStatusIds.map((statusId) => {
            return apis.ReadApplicationList({
              DistrictID: districtID,
              ApplicationStatusID: statusId,
              LoanType: this.selectedLoanType,
              MobileNo: "",
              ApplicationNo: "",
              StartDate: "01-01-1970",
              EndDate: "01-01-1970",
            }).then((r) => (r.status === 200 ? r.body || [] : [])).catch(() => []);
          });

          const loiPromise = apis.ReadSanctionLetterQuery({
            LoanApplicationNo: null,
            LOINumber: null,
            LOIStartDate: null,
            LOIEndDate: null,
            BankID: "00000000-0000-0000-0000-000000000000",
            DistrictID: districtID || "00000000-0000-0000-0000-000000000000",
            UserLoginID: null,
            RecordStartDate: null,
            RecordEndDate: null,
          }).then((r) => (r.status === 200 ? r.body || [] : [])).catch(() => []);

          const claimPromise = apis.ReadClaimEMIForApproval({
            DistrictID: districtID || "00000000-0000-0000-0000-000000000000",
            BankID: "00000000-0000-0000-0000-000000000000",
            ClaimStatus: 0,
            ClaimPaidStatus: 0,
            LoanApplicationNo: null,
            LOINumber: null,
            UserLoginID: null,
            RecoStatus: 0,
            ClaimStartDate: null,
            ClaimEndDate: null,
            LoanType: this.selectedLoanType,
            StatusOwner: "All",
          }).then((r) => (r.status === 200 ? r.body || [] : [])).catch(() => []);

          return Promise.all([Promise.all(appPromises), loiPromise, claimPromise]);
        })
        .then(([appResults, loiData, claimData]) => {
          let all = [].concat(...appResults);
          console.log("Applications:", all.length, "LOI records:", loiData.length, "Claim records:", claimData.length);

          // Log ALL field names from first record of each API for debugging
          if (all.length > 0) console.log("APP KEYS:", Object.keys(all[0]).join(", "));
          if (loiData.length > 0) console.log("LOI KEYS:", Object.keys(loiData[0]).join(", "));
          if (claimData.length > 0) console.log("CLAIM KEYS:", Object.keys(claimData[0]).join(", "));

          // Build LOI lookup by LoanApplicationNo
          const loiMap = {};
          loiData.forEach((loi) => {
            const key = (loi.LoanApplicationNo || "").trim();
            if (key) loiMap[key] = loi;
          });

          // Build Claim lookup by LoanApplicationNo (aggregate)
          const claimMap = {};
          claimData.forEach((c) => {
            const key = (c.LoanApplicationNo || "").trim();
            if (key) {
              if (!claimMap[key]) {
                claimMap[key] = { TotalClaimAmount: 0, TotalPaidClaim: 0, EMIPaidDate: "", ClaimStatus: "" };
              }
              claimMap[key].TotalClaimAmount += parseFloat(c.TotalClaimAmount) || 0;
              claimMap[key].TotalPaidClaim += parseFloat(c.TotalPaidClaim) || 0;
              claimMap[key].EMIPaidDate = c.EMIPaidDate || claimMap[key].EMIPaidDate;
              claimMap[key].ClaimStatus = c.ClaimStatus || claimMap[key].ClaimStatus;
            }
          });

          // 1) Filter by Financial Year
          all = all.filter((app) => {
            const raw = app.Createdate || app.CreateDate || app.ApplicationDate || "";
            const dt = new Date(raw);
            return dt >= fyStart && dt <= fyEnd;
          });

          // 2) Filter by caste codes (skip for Universal type companies)
          if (this.selectedCompany.type !== "Universal" && all.length > 0) {
            const validCasteCodes = casteCodes.filter((c) => c > 0);
            const sample = all[0];
            const numericField = ["CasteID", "CasteCode", "SubCasteID", "casteId", "casteCode", "SubCasteCode"]
              .find((f) => sample[f] !== undefined);
            const textField = ["CasteName", "Caste", "CasteMarathi", "casteName", "caste", "SubCaste", "SubCasteName"]
              .find((f) => sample[f] !== undefined);

            if (validCasteCodes.length > 0 && numericField) {
              all = all.filter((app) => validCasteCodes.includes(Number(app[numericField])));
            } else if (textField && casteNames.length > 0) {
              all = all.filter((app) =>
                casteNames.some((name) =>
                  (app[textField] || "").toLowerCase().includes(name) ||
                  name.includes((app[textField] || "").toLowerCase())
                )
              );
            }
          }

          // 3) Normalize fields + Merge LOI + Claim data
          all = all.map((app) => {
            const appNo = (app.LoanApplicationNo || "").trim();
            const loi = loiMap[appNo];
            const claim = claimMap[appNo];

            return {
              // Base fields — try multiple possible API key names
              CasteMarathi:      pick(app, ["CasteMarathi", "CasteName", "Caste", "SubCaste", "SubCasteName"], ""),
              BusinessType:      pick(app, ["BusinessType", "Business", "BusinessName"], ""),
              SchemeType:        pick(app, ["SchemeType", "SchemType", "SchemeName", "LoanTypeName"], ""),
              LoanApplicationNo: app.LoanApplicationNo || "",
              ADDRESS:           pick(app, ["ADDRESS", "Address", "FullAddress", "PermanentAddress", "Village", "Landmark"], ""),
              Taluka:            pick(app, ["Taluka", "TalukaName", "TalukName"], ""),
              ApplicationDate:   pick(app, ["ApplicationDate", "Createdate", "CreateDate", "CreatedDate"], ""),
              AmountDemanded:    app.AmountDemanded || 0,
              ApplicationStatus: app.ApplicationStatus || "",
              ApplicationStatus1: pick(app, ["ApplicationStatus1", "ApprovalStatus", "LastStatusDate"], ""),

              // LOI fields
              AmountApproved:    loi ? (loi.SanctionAmount || 0) : (app.AmountApproved || app.SanctionAmount || 0),
              LOINumber:         loi ? (loi.LOINumber || "") : "",
              LOIDate:           loi ? (loi.LOIDate || "") : "",
              LOIDueDate:        loi ? (loi.LOIDueDate || loi.DueDate || "") : "",
              BankName:          loi ? (loi.BankName || "") : "",
              Demand:            loi ? (loi.DisbursementAmount || 0) : 0,

              // Claim fields
              ClaimAmount:       claim ? claim.TotalClaimAmount : 0,
              AppliedForClaimDtd: claim ? (claim.EMIPaidDate || "") : "",
              PaidAmt:           claim ? claim.TotalPaidClaim : 0,
              Claim:             claim ? (claim.ClaimStatus || "") : "",
              PaidDt:            claim ? (claim.EMIPaidDate || "") : "",
            };
          });

          console.log("Final filtered records:", all.length);
          if (all.length > 0) console.log("Sample record:", JSON.stringify(all[0]));

          this.reportData = all;
          this.totalRows  = all.length;
          this.searched  = true;
          this.isLoading = false;
          this.currentPage = 1;
        })
        .catch((err) => {
          console.error("API Error:", err);
          this.searched  = true;
          this.isLoading = false;
        });
    },

    onFiltered(filteredItems) {
      this.totalRows  = filteredItems.length;
      this.currentPage = 1;
    },

    exportToExcel() {
      if (this.reportData.length === 0) return;

      const companyName = this.selectedCompany ? this.selectedCompany.name : "SubCompany";
      const fy          = this.selectedYear || "";
      const filename    = companyName + "_" + fy + ".xlsx";

      // Build export rows with readable headers
      const exportData = this.reportData.map((row, idx) => ({
        "Sr. No."              : idx + 1,
        "CasteMarathi"         : row.CasteMarathi        || "",
        "BusinessType"         : row.BusinessType        || "",
        "SchemeType"           : row.SchemeType           || "",
        "LoanApplicationNo"    : row.LoanApplicationNo   || "",
        "ADDRESS"              : row.ADDRESS              || "",
        "Taluka"               : row.Taluka               || "",
        "ApplicationDate"      : row.ApplicationDate      || "",
        "AmountDemanded"       : row.AmountDemanded       || "",
        "ApplicationStatus"    : row.ApplicationStatus    || "",
        "ApplicationStatus1"   : row.ApplicationStatus1   || "",
        "AmountApproved"       : row.AmountApproved       || "",
        "LOINumber"            : row.LOINumber            || "",
        "LOIDate"              : row.LOIDate              || "",
        "LOIDueDate"           : row.LOIDueDate           || "",
        "BankName"             : row.BankName             || "",
        "Demand"               : row.Demand               || "",
        "Claim Amount"         : row.ClaimAmount          || "",
        "Applied for Claim dtd": row.AppliedForClaimDtd   || "",
        "Paid Amt"             : row.PaidAmt              || "",
        "Claim"                : row.Claim                || "",
        "Paid dt"              : row.PaidDt               || "",
      }));

      const wb = XLSX.utils.book_new();

      // Title row, then data from A3
      const ws_data = [
        ["MAHARASHTRA RAJYA ITAR MAGASVARGIYA VITTA ANI VIKAS MAHAMANDAL LTD."],
        [companyName + " — Financial Year: " + fy],
      ];
      const ws = XLSX.utils.aoa_to_sheet(ws_data);

      // Merge title rows across columns
      ws["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 21 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 21 } },
      ];
      // Append data rows starting at row 3 (A3)
      XLSX.utils.sheet_add_json(ws, exportData, { skipHeader: false, origin: "A3" });

      // Add totals row at the end of data
      const dataEndRow = exportData.length + 3; // 2 title rows + 1 header row + data rows
      const totalsRow = [
        "TOTAL", "", "", "", "", "", "", "",
        this.sumAmountDemanded, "", "",
        this.sumAmountApproved, "", "", "", "",
        this.sumDemand,
        this.sumClaimAmount, "",
        this.sumPaidAmt, "", ""
      ];
      XLSX.utils.sheet_add_aoa(ws, [totalsRow], { origin: "A" + (dataEndRow + 1) });

      XLSX.utils.book_append_sheet(wb, ws, "Report");

      // ===== Summary Sheet =====
      const summaryData = [
        ["MAHARASHTRA RAJYA ITAR MAGASVARGIYA VITTA ANI VIKAS MAHAMANDAL LTD."],
        [companyName + " — Financial Year: " + fy + " — SUMMARY"],
        [],
        ["Description", "Count / Amount"],
        ["Total Applications", this.totalApplications],
        ["Total LOI Issued", this.totalLOI],
        ["Total Claims", this.totalClaim],
        [],
        ["Total Amount Demanded", parseFloat(this.sumAmountDemanded)],
        ["Total Amount Approved", parseFloat(this.sumAmountApproved)],
        ["Total Demand", parseFloat(this.sumDemand)],
        ["Total Claim Amount", parseFloat(this.sumClaimAmount)],
        ["Total Paid Amount", parseFloat(this.sumPaidAmt)],
      ];
      const ws2 = XLSX.utils.aoa_to_sheet(summaryData);
      ws2["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 1 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 1 } },
      ];
      ws2["!cols"] = [{ wch: 30 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, ws2, "Summary");

      XLSX.writeFile(wb, filename);
    },
  },

  mounted() {
    this.buildFinancialYears();
    this.detectTenantAndFilterCompanies();
  },
};
</script>

<style scoped>
.dash-heading {
  font-size: 16px;
  font-weight: 600;
  color: #2e4b90;
  padding: 8px 0;
}
</style>



// WEBPACK FOOTER //
// src/components/AdminReports/SubCompanyReport.vue